import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { api } from './api'
import { TOKEN_KEY } from './types'
import {
  clearAuthSession,
  detectAuthPersist,
  getAuthItem,
  persistAuthSession,
  setAuthItem,
} from '../shared/authStorage'

const CLIENT_KEY = 'pp_client'
const HANDOFF_RE = /(?:^|#|&)pp_handoff=([^&]+)/
const HANDOFF_TOKEN_RE = /^[A-Za-z0-9_-]{20,256}$/

function readHandoffToken(): string | null {
  if (typeof window === 'undefined') return null
  const raw = HANDOFF_RE.exec(window.location.hash || '')?.[1] || ''
  const token = decodeURIComponent(raw).trim()
  if (!HANDOFF_TOKEN_RE.test(token)) return null
  const clean = `${window.location.pathname}${window.location.search}`
  window.history.replaceState(null, '', clean)
  return token
}

export type ClientProfile = {
  id?: number
  email?: string
  firstName?: string
  lastName?: string
  emailVerified?: boolean
}

type AuthContextValue = {
  token: string | null
  client: ClientProfile | null
  isAuthenticated: boolean
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>
  loginWithGoogle: (credential: string, rememberMe?: boolean, ibCode?: string) => Promise<void>
  acceptSession: (
    accessToken: string,
    client?: Record<string, unknown> | null,
    rememberMe?: boolean,
  ) => void
  logout: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

function readStoredClient(): ClientProfile | null {
  try {
    const raw = getAuthItem(CLIENT_KEY)
    if (!raw) return null
    return JSON.parse(raw) as ClientProfile
  } catch {
    return null
  }
}

function normalizeClient(raw?: Record<string, unknown> | null): ClientProfile | null {
  if (!raw) return null
  return {
    id: typeof raw.id === 'number' ? raw.id : undefined,
    email: typeof raw.email === 'string' ? raw.email : undefined,
    firstName: typeof raw.firstName === 'string' ? raw.firstName : undefined,
    lastName: typeof raw.lastName === 'string' ? raw.lastName : undefined,
    emailVerified: Boolean(raw.emailVerified),
  }
}

function storeClientSession(
  accessToken: string,
  rawClient: Record<string, unknown> | null | undefined,
  rememberMe: boolean,
) {
  const profile = normalizeClient(rawClient || null)
  persistAuthSession({
    tokenKey: TOKEN_KEY,
    userKey: CLIENT_KEY,
    token: accessToken,
    userJson: profile ? JSON.stringify(profile) : null,
    rememberMe,
  })
  return profile
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => {
    const handoff = readHandoffToken()
    if (handoff) {
      persistAuthSession({
        tokenKey: TOKEN_KEY,
        userKey: CLIENT_KEY,
        token: handoff,
        userJson: null,
        rememberMe: true,
      })
      return handoff
    }
    return getAuthItem(TOKEN_KEY)
  })
  const [client, setClient] = useState<ClientProfile | null>(() => readStoredClient())

  const login = useCallback(async (email: string, password: string, rememberMe = true) => {
    const res = await api.login(email, password)
    const profile = storeClientSession(res.accessToken, res.client || null, rememberMe)
    setToken(res.accessToken)
    setClient(profile)
  }, [])

  const loginWithGoogle = useCallback(
    async (credential: string, rememberMe = true, ibCode = '') => {
      const res = await api.loginWithGoogle(credential, ibCode)
      const profile = storeClientSession(res.accessToken, res.client || null, rememberMe)
      setToken(res.accessToken)
      setClient(profile)
    },
    [],
  )

  const acceptSession = useCallback(
    (accessToken: string, rawClient?: Record<string, unknown> | null, rememberMe = true) => {
      const profile = storeClientSession(accessToken, rawClient, rememberMe)
      setToken(accessToken)
      setClient(profile)
    },
    [],
  )

  const logout = useCallback(() => {
    clearAuthSession(TOKEN_KEY, CLIENT_KEY)
    setToken(null)
    setClient(null)
  }, [])

  const value = useMemo(
    () => ({
      token,
      client,
      isAuthenticated: Boolean(token),
      login,
      loginWithGoogle,
      acceptSession,
      logout,
    }),
    [token, client, login, loginWithGoogle, acceptSession, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}

export function touchClientProfile(profile: ClientProfile | null) {
  const mode = detectAuthPersist(TOKEN_KEY)
  if (!profile) {
    clearAuthSession(TOKEN_KEY, CLIENT_KEY)
    return
  }
  setAuthItem(CLIENT_KEY, JSON.stringify(profile), mode)
}
